<template>
  <section class="trophies container-fluid p-0 m-0">
    <div v-if="showSkeleton" class="banner-skeleton-block" aria-hidden="true"></div>
    <div v-else class="banner banner-reveal" :class="{ 'is-visible': contentVisible }">
      <img
        :src="bannerImage"
        alt="Banner Trophies"
        class="img-fluid w-100"
      />
    </div>
  </section>
  <section id="trophiesBody" class="container-fluid d-flex justify-content-center">
    <div v-if="showSkeleton" class="row gap-4 w-100 d-flex justify-content-center trophies-row" aria-hidden="true">
      <div
        v-for="i in 3"
        :key="`skeleton-${i}`"
        class="col-12 d-flex justify-content-center"
      >
        <div class="trophy-skeleton-item">
          <div class="trophy-skeleton-logo"></div>
          <div class="trophy-skeleton-recuadro"></div>
        </div>
      </div>
    </div>

    <div
      v-else
      class="row w-100 d-flex justify-content-center trophies-row trophies-content"
      :class="{ 'is-visible': contentVisible }"
    >
      <div
        v-for="(trophy, index) in trophies"
        :key="trophy.id || index"
        class="col-12 d-flex justify-content-center"
      >
        <div class="trophy-item" :class="`trophy-item-${plateKey(trophy.plate_size)}`">
          <img
            v-if="trophy.logo_url"
            class="trophy-logo"
            :src="trophy.logo_url"
            :alt="trophy.name"
          />
          <div class="recuadro" :class="`recuadro-${plateKey(trophy.plate_size)}`">
            <img class="recuadro-bg" :src="plateSrc(trophy.plate_size)" alt="" aria-hidden="true" />
            <div class="recuadro-content">
              <div class="recuadro-head">
                <span v-if="trophy.name" class="recuadro-name">{{ trophy.name }}</span>
                <div class="recuadro-trophy">
                  <span v-if="trophy.entry_count" class="recuadro-count">{{ trophy.entry_count }}</span>
                  <img class="recuadro-trophy-icon" :src="trophyIcon" alt="Copas ganadas" />
                </div>
              </div>
              <ul v-if="trophy.cup_entries && trophy.cup_entries.length" class="recuadro-entries">
                <li v-for="(entry, i) in trophy.cup_entries" :key="i">-{{ entry }}</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
<script>
import { fetchPublicTrophies } from '@/services/trophies';

const TROPHIES_CACHE_KEY = 'trophiesContentReady';
const MIN_SKELETON_MS = 480;

const PLATES = {
  L: require('@/assets/images/premios/Placa Trofeo L.svg'),
  X: require('@/assets/images/premios/Placa Trofeo X.svg'),
  XL: require('@/assets/images/premios/Placa Trofeo XL.svg'),
  XXL: require('@/assets/images/premios/Placa Trofeo XXL.svg'),
};
const TROPHY_ICON = require('@/assets/images/premios/trofeo.svg');

const waitMinDuration = (startedAt, minMs = MIN_SKELETON_MS) => {
  const remaining = minMs - (Date.now() - startedAt);
  if (remaining <= 0) return Promise.resolve();
  return new Promise((resolve) => setTimeout(resolve, remaining));
};

const loadImage = (src) => new Promise((resolve) => {
  const img = new Image();
  img.onload = () => resolve();
  img.onerror = () => resolve();
  img.src = src;
});

export default {
  name: 'TrophiesView',
  data() {
    const contentCached = sessionStorage.getItem(TROPHIES_CACHE_KEY) === 'true';

    return {
      isMobile: window.innerWidth <= 768,
      showSkeleton: !contentCached,
      contentVisible: false,
      trophies: [],
      trophyIcon: TROPHY_ICON,
    };
  },
  computed: {
    bannerImage() {
      return this.isMobile
        ? require('@/assets/images/banners/banner-intro-copas-tel.svg')
        : require('@/assets/images/banners/banner-intro-copas.svg');
    },
    imageSources() {
      const sources = [this.bannerImage, this.trophyIcon];
      this.trophies.forEach((trophy) => {
        const plate = this.plateSrc(trophy.plate_size);
        if (plate) sources.push(plate);
        if (trophy.logo_url) sources.push(trophy.logo_url);
      });
      return sources;
    },
  },
  mounted() {
    window.addEventListener('resize', this.checkScreenSize);
    this.loadTrophies();
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.checkScreenSize);
  },
  watch: {
    bannerImage() {
      if (this.showSkeleton) return;
      this.contentVisible = false;
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          this.contentVisible = true;
        });
      });
    },
  },
  methods: {
    plateKey(size) {
      const key = String(size || '').toUpperCase();
      return ['L', 'X', 'XL', 'XXL'].includes(key) ? key : 'L';
    },
    plateSrc(size) {
      return PLATES[this.plateKey(size)];
    },
    async loadTrophies() {
      const startedAt = Date.now();
      let data = [];
      try {
        data = await fetchPublicTrophies();
      } catch (err) {
        console.error('Error al cargar los trofeos:', err);
        data = [];
      }
      this.trophies = Array.isArray(data) ? data : [];
      if (this.showSkeleton) {
        await Promise.all(this.imageSources.map((src) => loadImage(src)));
        await waitMinDuration(startedAt);
        sessionStorage.setItem(TROPHIES_CACHE_KEY, 'true');
        this.showSkeleton = false;
      }
      await this.revealContent();
    },
    async revealContent() {
      await this.$nextTick();
      requestAnimationFrame(() => {
        this.contentVisible = true;
      });
    },
    checkScreenSize() {
      this.isMobile = window.innerWidth <= 768;
    },
  },
};
</script>

<style scoped>
#trophiesBody {
  min-height: 70vh;
  padding: 2rem 1rem;
  background-image: url('@/assets/images/fondos/fondo-copas.svg');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  margin-top: -60px;
  overflow: hidden;
}

.banner-reveal,
.trophies-content {
  opacity: 0;
  transition: opacity 0.55s ease;
  will-change: opacity;
}

.banner-reveal.is-visible,
.trophies-content.is-visible {
  opacity: 1;
}

.banner-skeleton-block {
  width: 100%;
  min-height: 180px;
  background: linear-gradient(90deg, rgba(51, 51, 51, 0.6) 25%, rgba(64, 64, 64, 0.8) 50%, rgba(51, 51, 51, 0.6) 75%);
  background-size: 200% 100%;
  animation: trophy-skeleton-loading 1.5s infinite;
}

.trophy-skeleton-item {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: clamp(1rem, 3vw, 2.5rem);
  width: min(1100px, 100%);
}

.trophy-skeleton-logo {
  flex: 0 0 auto;
  width: 140px;
  height: 140px;
  background: linear-gradient(90deg, rgba(51, 51, 51, 0.6) 25%, rgba(64, 64, 64, 0.8) 50%, rgba(51, 51, 51, 0.6) 75%);
  background-size: 200% 100%;
  animation: trophy-skeleton-loading 1.5s infinite;
}

.trophy-skeleton-recuadro {
  flex: 1 1 auto;
  aspect-ratio: 247.22 / 111.56;
  border-radius: 6px;
  background: linear-gradient(90deg, rgba(51, 51, 51, 0.6) 25%, rgba(64, 64, 64, 0.8) 50%, rgba(51, 51, 51, 0.6) 75%);
  background-size: 200% 100%;
  animation: trophy-skeleton-loading 1.5s infinite;
}

/* ── Item: logo (afuera) + recuadro (derecha) ───────────────────────── */
.trophy-item {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: clamp(1rem, 3vw, 2.5rem);
  width: auto;
  max-width: 100%;
}

/* Diferentes tamaños para los trofeos */
/* Diferentes tamaños para los trofeos */
.trophy-item {
  --trophy-h: 70px;
  --trophy-size: 1.3rem;
  --count-size: 1.1rem;
}

.trophy-item-L {
  --logo-h: 140px;
  --logo-w: 180px;
  --recuadro-w: 550px;
  --text-clear: 20px;
}
.trophy-item-X {
  --logo-h: 175px;
  --logo-w: 220px;
  --recuadro-w: 550px;
  --text-clear: 110px;
}
.trophy-item-XL {
  --logo-h: 205px;
  --logo-w: 260px;
  --recuadro-w: 550px;
  --text-clear: 130px;
}
.trophy-item-XXL {
  --logo-h: 250px;
  --logo-w: 320px;
  --recuadro-w: 550px;
  --text-clear: 150px;
}

/* Logo independiente: hermano del recuadro, NO dentro de la placa */
.trophy-logo {
  flex: 0 0 auto;
  height: auto;
  width: 400px;
  object-fit: contain;
}

/* ── Recuadro (placa dorada) ────────────────────────────────────────── */
.recuadro {
  position: relative;
  flex: 0 0 auto;
  width: var(--recuadro-w, 700px);
  overflow: hidden;
}

.recuadro-L { aspect-ratio: 247.17 / 53.44; }
.recuadro-X { aspect-ratio: 247.17 / 74.29; }
.recuadro-XL { aspect-ratio: 247.22 / 85.75; }
.recuadro-XXL { aspect-ratio: 247.22 / 111.56; }

.recuadro-bg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  display: block;
}

.recuadro-content {
  position: absolute;
  top: var(--recuadro-content-top, 15px);
  bottom: 7%;
  left: 3.2%;
  right: 3.2%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 0.2em;
}

.recuadro-head {
  display: block;
  position: relative;
  flex-shrink: 0;
}

.recuadro-name {
  font-family: 'Dinn', 'Lufga', sans-serif;
  font-weight: 700;
  font-size: 1.1rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #663f07e8;
  line-height: 1.05;
  flex: 1 1 auto;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding-right: var(--text-clear);
}

.recuadro-trophy {
  position: absolute;
  top: 0;
  right: 0;
  display: inline-flex;
}

.recuadro-trophy-icon {
  height: var(--trophy-h);
  width: auto;
  display: block;
}

.recuadro-count {
  position: absolute;
  top: 15%;
  left: 50%;
  transform: translateX(-50%);
  min-width: 2em;
  padding: 0.18em 0.45em;
  border-radius: 999px;
  color: #4b2e05bb;
  font-weight: 300;
  font-size: 1.1rem;
  line-height: 1;
  text-align: center;
  font-family: 'Dinn', sans-serif;
  z-index: 2;
}

.recuadro-entries {
  list-style: none;
  margin: 0;
  padding: 0;
  overflow: hidden;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 0.28em;
}

.recuadro-entries li {
  font-size: .9rem;
  font-weight: 550;
  color: #4b2e05bb;
  padding-right: 20px;
  line-height: 1;
  text-shadow: 0 0 6px rgba(255, 238, 0, 0.644);
  font-family: 'Dinn', sans-serif;
  letter-spacing: 0.02em;
}

@media (max-width: 768px) {
  .recuadro-entries li {
  font-size: 0.5rem;
  line-height: 1.1;
}
.recuadro-name {
  font-size: 0.9rem;
  line-height: 1.1;
}
} 

@keyframes trophy-skeleton-loading {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

@media (max-width: 768px) {
  #trophiesBody {
    padding: 1rem 0.5rem;
    margin-top: 0px;
  }

  .banner-skeleton-block {
    min-height: 120px;
  }

  .trophy-item,
  .trophy-skeleton-item {
    gap: 0.8rem;
  }
  .trophy-logo {
    width: 300px;
  }
  /* Tamaños redimensionados para tablets */
  .trophy-item-L {
    --logo-h: 84px;
    --logo-w: 110px;
    --recuadro-w: 360px;
    --text-clear: 70px;
  }
  .trophy-item-X {
    --logo-h: 104px;
    --logo-w: 135px;
    --recuadro-w: 360px;
    --text-clear: 80px;
  }
  .trophy-item-XL {
    --logo-h: 122px;
    --logo-w: 160px;
    --recuadro-w: 360px;
    --text-clear: 90px;
  }
  .trophy-item-XXL {
    --logo-h: 150px;
    --logo-w: 195px;
    --recuadro-w: 360px;
    --text-clear: 100px;
  }

  .trophy-skeleton-logo {
    width: 84px;
    height: 84px;
  }

  .recuadro-content {
    bottom: 5%;
    left: 2.5%;
    right: 2.5%;
    gap: 0.2em;
  }

  .recuadro-name {
    white-space: normal;
    padding-right: var(--text-clear);
  }
}

@media (max-width: 480px) {
  /* Apilar logo arriba y placa abajo en móviles */
  .trophy-item,
  .trophy-skeleton-item {
    flex-wrap: wrap;
    gap: 0.6rem;
  }

  .trophy-item {
    --trophy-h: 55px;
  }

  /* Tamaños redimensionados para móviles */
  .trophy-item-L {
    --logo-h: 52px;
    --logo-w: 65px;
    --recuadro-w: 240px;
    --text-clear: 50px;
  }
  .trophy-item-X {
    --logo-h: 64px;
    --logo-w: 82px;
    --recuadro-w: 240px;
    --text-clear: 58px;
  }
  .trophy-item-XL {
    --logo-h: 76px;
    --logo-w: 98px;
    --recuadro-w: 240px;
    --text-clear: 65px;
  }
  .trophy-item-XXL {
    --logo-h: 96px;
    --logo-w: 125px;
    --recuadro-w: 240px;
    --text-clear: 75px;
  }

  .trophy-skeleton-logo {
    width: 52px;
    height: 52px;
  }

  .recuadro-content {
    gap: 0.2em;
  }

  .recuadro-name {
    padding-right: var(--text-clear);
  }

  .recuadro {
    flex-basis: 100%;
  }
}

@media (max-width: 380px) {
  /* Último recurso: apilar (logo arriba, placa ancho completo abajo) */
  .trophy-item,
  .trophy-skeleton-item {
    flex-wrap: wrap;
    gap: 0.7rem;
  }

  /* Tamaños redimensionados para móviles muy pequeños */
  .trophy-item-L {
    --logo-h: 60px;
    --logo-w: 75px;
    --recuadro-w: 200px;
    --text-clear: 45px;
  }
  .trophy-item-X {
    --logo-h: 72px;
    --logo-w: 90px;
    --recuadro-w: 200px;
    --text-clear: 52px;
  }
  .trophy-item-XL {
    --logo-h: 84px;
    --logo-w: 108px;
    --recuadro-w: 200px;
    --text-clear: 58px;
  }
  .trophy-item-XXL {
    --logo-h: 110px;
    --logo-w: 140px;
    --recuadro-w: 200px;
    --text-clear: 68px;
  }

  .trophy-skeleton-logo {
    width: 64px;
    height: 64px;
  }

  .recuadro-content {
    gap: 0.2em;
  }

  .recuadro {
    flex-basis: 100%;
  }
}
</style>
